"use client";

import Link from "next/link";
import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import { createClient } from "@/lib/supabase/client";

export default function SignupPage() {
  return (
    <Suspense>
      <SignupContent />
    </Suspense>
  );
}

function SignupContent() {
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") || "";
  const supabase = createClient();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function signInWithGoogle() {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
      },
    });
  }

  async function signUpWithEmail(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("パスワードは8文字以上で入力してください。");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
      },
    });
    setLoading(false);
    if (error) {
      setError(translateAuthError(error.message));
      return;
    }
    window.location.href = `/signup/confirm?email=${encodeURIComponent(email)}`;
  }

  function translateAuthError(message: string): string {
    if (message.includes("weak") || message.includes("easy to guess")) {
      return "パスワードが簡単すぎます。より複雑なパスワードを設定してください。";
    }
    if (message.includes("already")) {
      return "このメールアドレスは既に登録されています。ログインするか、Googleアカウントでお試しください。";
    }
    if (message.includes("valid email")) {
      return "有効なメールアドレスを入力してください。";
    }
    if (message.includes("rate limit")) {
      return "リクエストが多すぎます。しばらく待ってからもう一度お試しください。";
    }
    return "登録に失敗しました。入力内容を確認してもう一度お試しください。";
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      {/* ヒーロー */}
      <div className="text-center mb-10">
        <h1 className="text-2xl font-bold mb-3">
          気になる物件に空室が出たら
          <br />
          すぐにお知らせします
        </h1>
        <p className="text-muted text-sm">
          UR賃貸・JKK東京の空室通知を無料で受け取れます
        </p>
      </div>

      {/* メリット */}
      <div className="bg-accent/10 rounded-lg p-5 mb-8 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-accent">✓</span>
          空室が出た瞬間にメールで通知
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-accent">✓</span>
          気になる物件を最大20件まで登録
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-accent">✓</span>
          完全無料・広告メールなし
        </div>
      </div>

      {/* Google ログイン */}
      <button
        onClick={signInWithGoogle}
        className="w-full flex items-center justify-center gap-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg py-3 px-4 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Googleアカウントで登録する
      </button>

      {/* セパレーター */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
        <span className="text-xs text-muted">または</span>
        <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
      </div>

      {/* メール/パスワード */}
      <form onSubmit={signUpWithEmail} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            メールアドレス
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="example@mail.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            パスワード（8文字以上）
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-accent text-white rounded-lg py-2.5 text-sm font-medium hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          {loading ? "登録中..." : "メールアドレスで登録する"}
        </button>
      </form>

      <p className="text-center text-sm text-muted mt-6">
        すでにアカウントをお持ちの方は{" "}
        <Link
          href={`/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
          className="text-accent hover:underline"
        >
          こちらからログイン
        </Link>
      </p>

      {/* 使い方 */}
      <div className="mt-12 border-t border-gray-200 dark:border-gray-700 pt-8">
        <h2 className="text-lg font-bold mb-6 text-center">使い方（3ステップ）</h2>
        <div className="space-y-4">
          {[
            { step: "1", title: "アカウントを登録", desc: "Googleアカウントまたはメールアドレスで簡単登録" },
            { step: "2", title: "気になる物件で「空室通知を受け取る」をタップ", desc: "最大20件まで登録できます" },
            { step: "3", title: "空室が出たらメールでお知らせ", desc: "先着順の物件も見逃しません" },
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                {item.step}
              </div>
              <div>
                <p className="font-medium text-sm">{item.title}</p>
                <p className="text-xs text-muted mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-10 border-t border-gray-200 dark:border-gray-700 pt-8">
        <h2 className="text-lg font-bold mb-4 text-center">よくある質問</h2>
        <div className="space-y-4 text-sm">
          {[
            { q: "料金はかかりますか？", a: "完全無料です。" },
            { q: "広告メールは届きますか？", a: "空室通知以外のメールは一切送りません。" },
            { q: "退会はできますか？", a: "マイページからいつでもアカウントを削除できます。" },
          ].map((faq) => (
            <div key={faq.q}>
              <p className="font-medium">Q. {faq.q}</p>
              <p className="text-muted mt-1">A. {faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
